<template>
  <el-dialog 
    :title="`合同预览 - ${fileName}`"
    :visible="visible"
    :close-on-click-modal="false"
    :modal="false"
    @close="closeDialog()"
    width="90%"
    top="5vh"
    fullscreen
  >
    <div v-if="type === 'pdf'" class="pdf-viewer-container">
      <!-- 工具栏 -->
      <div class="pdf-toolbar">
        <el-button-group>
          <el-button 
            size="small" 
            @click="zoomOut" 
            :disabled="pdfInfo.scale <= 0.5"
          >
            <i class="el-icon-zoom-out"></i> 缩小
          </el-button>
          <el-button size="small">
            缩放: {{ (pdfInfo.scale * 100).toFixed(0) }}%
          </el-button>
          <el-button 
            size="small" 
            @click="zoomIn" 
            :disabled="pdfInfo.scale >= 2.0"
          >
            <i class="el-icon-zoom-in"></i> 放大
          </el-button>
        </el-button-group>
        
        <div class="page-count">
          共 {{ pdfInfo.pages.length }} 页
        </div>
      </div>
      
      <!-- PDF页面容器 -->
      <div class="pdf-pages-container" ref="pdfPages">
        <div v-if="pdfInfo.loading" class="loading-tip">
          <i class="el-icon-loading"></i>
          <span>正在渲染PDF页面...</span>
        </div>
      </div>
    </div>

    <div v-else-if="type === 'docx'" class="preview-container">
      <vue-office-docx 
        :src="url" 
        style="height: 80vh;"
      />
    </div>
    
    <div slot="footer" class="dialog-footer">
      <el-button @click="closeDialog()">关闭</el-button>
      <el-button 
        type="primary" 
        @click="$emit('download', {contract: url})"
      >
        下载
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import VueOfficeDocx from '@vue-office/docx'
import * as pdfjsLib from 'pdfjs-dist/webpack'

export default {
  name: 'ContractPreviewDialog',
  components: {
    VueOfficeDocx
  },
  props: {
    visible: Boolean,
    type: String, // 'pdf' 或 'docx'
    url: String
  },
  data() {
    return {
      pdfInfo: {
        pages: [],     // 存储所有页面
        scale: 1.0,    // 默认缩放比例
        pdfDoc: null,  // PDF文档对象
        loading: false // 加载状态
      }
    }
  },
  computed: {
    fileName() {
      if (!this.url) return '';
      return this.url.split('/').pop();
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.type === 'pdf') {
        this.previewPdf();
      }
    }
  },
  methods: {
    closeDialog() {
      this.$emit('update:visible', false);
    },
    async previewPdf() {
      try {
        // 初始化状态
        this.pdfInfo.loading = true;
        this.pdfInfo.pages = [];
        
        // 等待对话框渲染
        await this.$nextTick();
        
        // 加载PDF.js
        if (!window.pdfjsLib) {
          await this.loadPdfJs();
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        // 加载PDF文档
        const loadingTask = pdfjsLib.getDocument(this.url);
        this.pdfInfo.pdfDoc = await loadingTask.promise;
        
        // 清空容器
        this.$refs.pdfPages.innerHTML = '';
        
        // 渲染所有页面
        for (let i = 1; i <= this.pdfInfo.pdfDoc.numPages; i++) {
          await this.renderPdfPage(i);
        }
        
      } catch (error) {
        console.error('PDF预览失败:', error);
        this.$message.error(`预览失败: ${error.message}`);
      } finally {
        this.pdfInfo.loading = false;
      }
    },

    async renderPdfPage(pageNum) {
      try {
        // 获取PDF页面
        const page = await this.pdfInfo.pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: this.pdfInfo.scale });
        
        // 创建canvas
        const canvas = document.createElement('canvas');
        canvas.className = 'pdf-page-canvas';
        const context = canvas.getContext('2d');
        
        // 设置canvas尺寸
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        // 渲染页面
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;
        
        // 添加到DOM
        this.$refs.pdfPages.appendChild(canvas);
        
        // 添加页面间距
        const spacer = document.createElement('div');
        spacer.className = 'page-spacer';
        this.$refs.pdfPages.appendChild(spacer);
        
        // 存储页面信息
        this.pdfInfo.pages.push({
          number: pageNum,
          canvas: canvas,
          height: viewport.height
        });
        
      } catch (error) {
        console.error(`渲染第${pageNum}页失败:`, error);
      }
    },

    async zoomIn() {
      this.pdfInfo.scale += 0.1;
      await this.rerenderAllPages();
    },
    
    async zoomOut() {
      if (this.pdfInfo.scale > 0.5) {
        this.pdfInfo.scale -= 0.1;
        await this.rerenderAllPages();
      }
    },
    
    async rerenderAllPages() {
      if (!this.pdfInfo.pdfDoc) return;
      
      this.pdfInfo.loading = true;
      try {
        // 清空容器
        this.$refs.pdfPages.innerHTML = '';
        this.pdfInfo.pages = [];
        
        // 重新渲染所有页面
        for (let i = 1; i <= this.pdfInfo.pdfDoc.numPages; i++) {
          await this.renderPdfPage(i);
        }
      } finally {
        this.pdfInfo.loading = false;
      }
    },

    // 添加加载PDF.js的方法
    loadPdfJs() {
      return new Promise((resolve) => {
        if (window.pdfjsLib) {
          resolve();
          return;
        }
        
        const script = document.createElement('script');
        script.src = `${process.env.BASE_URL}utils/pdf.worker.min.js`
        script.onload = () => {
          pdfjsLib.GlobalWorkerOptions.workerSrc = 
            `${process.env.BASE_URL}utils/pdf.worker.min.js`;
          resolve();
        };
        document.head.appendChild(script);
      });
    }
  }
}
</script>

<style scoped>
.pdf-viewer-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.pdf-toolbar {
  padding: 8px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  z-index: 1;
  display: flex;
  justify-content: center; /* 改为居中 */
  align-items: center;
  gap: 10px; /* 添加按钮间距 */
}

.pdf-pages-container {
  flex: 1;
  overflow-y: auto;
  position: relative;
  background: #f9f9f9;
  display: flex;
  flex-direction: column;
  align-items: center; /* 使内容居中 */
  padding: 20px 0; /* 添加上下内边距 */
}

.pdf-page-canvas {
  display: block;
  margin: 0 auto 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: white;
}

.loading-tip {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #606266;
  display: flex;
  align-items: center;
}

.page-spacer {
  height: 20px;
}

.preview-container {
  height: 80vh;
  overflow: auto;
  border: 1px solid #eaeaea;
  border-radius: 4px;
  padding: 10px;
  background-color: #f5f5f5;
  display: flex;
  justify-content: center; /* 使内容居中 */
}

/* 确保DOCX内容居中 */
.preview-container >>> .docx-wrapper {
  margin: 0 auto;
}

.dialog-footer {
  text-align: center;
}
</style>